import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass, X, Plus } from "@phosphor-icons/react";

/**
 * Combobox de texto libre con sugerencias: permite elegir un valor existente
 * o crear uno nuevo escribiéndolo (p.ej. proveedor nuevo).
 *
 * Props:
 * - value: string
 * - onChange: (v: string) => void
 * - options: string[]  (sugerencias)
 * - placeholder: string
 * - allowClear: bool (default true)
 * - disabled: bool
 * - className: string
 * - "data-testid": string
 */
export default function CreatableSelect({
    value,
    onChange,
    options = [],
    placeholder = "Seleccionar o escribir...",
    allowClear = true,
    disabled = false,
    className = "",
    ...rest
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const tid = rest["data-testid"];

    const filtered = useMemo(() => {
        if (!query.trim()) return options;
        const q = query.trim().toLowerCase();
        return options.filter((o) => String(o).toLowerCase().includes(q));
    }, [options, query]);

    const exactMatch = useMemo(
        () => options.some((o) => String(o).toLowerCase() === query.trim().toLowerCase()),
        [options, query],
    );

    useEffect(() => {
        const onClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 10);
        else setQuery("");
    }, [open]);

    const pick = (v) => {
        onChange(v);
        setOpen(false);
    };

    return (
        <div ref={wrapRef} className="relative" data-testid={tid}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                className={`w-full flex items-center justify-between border border-slate-300 rounded-sm px-3 py-2 bg-white text-sm text-left hover:border-slate-400 disabled:opacity-50 ${className}`}
                data-testid={tid ? `${tid}-trigger` : undefined}
            >
                <span className={value ? "truncate text-slate-900" : "text-slate-400"}>
                    {value || placeholder}
                </span>
                <span className="flex items-center gap-1 flex-shrink-0 ml-2">
                    {allowClear && value && !disabled && (
                        <span
                            role="button"
                            tabIndex={-1}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange("");
                            }}
                            className="text-slate-400 hover:text-slate-600 p-0.5"
                            data-testid={tid ? `${tid}-clear` : undefined}
                        >
                            <X size={12} />
                        </span>
                    )}
                    <CaretDown size={12} className="text-slate-400" />
                </span>
            </button>
            {open && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-hidden flex flex-col">
                    <div className="relative border-b border-slate-100">
                        <MagnifyingGlass
                            size={12}
                            className="absolute left-2 top-2.5 text-slate-400"
                        />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && query.trim()) {
                                    e.preventDefault();
                                    pick(query.trim());
                                }
                            }}
                            placeholder="Escribe para buscar o crear..."
                            className="w-full pl-7 pr-2 py-2 text-sm outline-none"
                            data-testid={tid ? `${tid}-search` : undefined}
                        />
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {query.trim() && !exactMatch && (
                            <button
                                type="button"
                                onClick={() => pick(query.trim())}
                                className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-[#002BAF] hover:bg-[#002BAF]/5 font-medium border-b border-slate-100"
                                data-testid={tid ? `${tid}-create` : undefined}
                            >
                                <Plus size={13} /> Crear «{query.trim()}»
                            </button>
                        )}
                        {filtered.length === 0 && !query.trim() ? (
                            <div className="text-center py-6 text-xs text-slate-400">
                                Sin sugerencias
                            </div>
                        ) : (
                            filtered.slice(0, 200).map((o) => (
                                <button
                                    type="button"
                                    key={o}
                                    onClick={() => pick(o)}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 truncate ${
                                        o === value
                                            ? "bg-[#002BAF]/5 text-[#002BAF] font-medium"
                                            : "text-slate-700"
                                    }`}
                                    data-testid={tid ? `${tid}-option` : undefined}
                                >
                                    {o}
                                </button>
                            ))
                        )}
                        {filtered.length > 200 && (
                            <div className="text-center py-2 text-[10px] text-slate-400 border-t border-slate-100">
                                Mostrando 200 de {filtered.length}. Refina la búsqueda.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
